import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ToastMessageOptions } from 'primeng/api';
import { FcmMessagingService } from '@/core/push-notification/fcm-messaging.service';
import { TranslationService } from '@/core/i18n/translation.service';

@Component({
    selector: 'app-toast',
    standalone: true,
    imports: [CommonModule, ToastModule, ButtonModule],
    template: `
        <p-toast [key]="key" position="bottom-right" [breakpoints]="{'920px': {width: '100%', right: '0', left: '0'}}">
            <ng-template let-message pTemplate="message">
                <div class="tms-toast">
                    @if (message?.data?.image) {
                        <div class="tms-toast__media" (click)="onImageClick(message)">
                            <img class="tms-toast__img" [src]="message.data.image" alt="" />
                        </div>
                    }
                    <div class="tms-toast__content">
                        <div class="tms-toast__summary">{{ message.summary }}</div>
                        @if (message.detail) {
                            <div class="tms-toast__detail">{{ message.detail }}</div>
                        }
                        @if (message?.data?.url) {
                            <div class="tms-toast__actions">
                                <button
                                    type="button"
                                    pButton
                                    size="small"
                                    severity="secondary"
                                    (click)="onViewClick(message)">
                                    {{ t('common.view', 'View') }}
                                </button>
                            </div>
                        }
                    </div>
                </div>
            </ng-template>
        </p-toast>
    `,
    styles: [
        `
            .tms-toast {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
                width: 100%;
            }

            .tms-toast__media {
                flex: 0 0 auto;
                cursor: pointer;
            }

            .tms-toast__img {
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 0.375rem;
                object-fit: cover;
            }

            .tms-toast__content {
                min-width: 0;
                flex: 1;
            }

            .tms-toast__summary {
                font-weight: 600;
                line-height: 1.3;
                word-break: break-word;
            }

            .tms-toast__detail {
                margin-top: 0.2rem;
                line-height: 1.3;
                word-break: break-word;
            }

            .tms-toast__actions {
                margin-top: 0.4rem;
            }

            :host ::ng-deep .p-toast-message-content {
                align-items: flex-start !important;
                gap: 0 !important;
            }

            :host ::ng-deep .p-toast-message-content > .p-toast-message-text {
                flex: 1;
                min-width: 0;
            }

            :host ::ng-deep .p-toast-message-close {
                align-self: flex-start !important;
                margin-top: 0 !important;
            }
        `
    ]
})
export class AppToastComponent {
    @Input() key: string = 'fcm';

    private fcmService = inject(FcmMessagingService);
    readonly translationService = inject(TranslationService);
    readonly t = this.translationService.translate.bind(this.translationService);

    onViewClick(message: ToastMessageOptions): void {
        const url = (message as any)?.data?.url as string | undefined;
        if (url) {
            this.fcmService.navigateTo(url);
        }
    }

    onImageClick(message: ToastMessageOptions): void {
        const image = (message as any)?.data?.image as string | undefined;
        if (image) {
            this.fcmService.openImage(image);
        }
    }
}
